import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Sidebar } from '../../shared/components/sidebar/sidebar';
import { Navbar } from '../../shared/components/navbar/navbar';
import { Toast } from '../../shared/services/toast';
import { Toast as ToastComponent } from '../../shared/components/toast/toast';
import {
  AiSources as AiSourcesService,
  AiToolSource,
  CustomSourceAuthType,
  CustomSourceHttpMethod,
  CustomSourceParameter,
  CustomSourceTestResponse,
  CustomToolSource,
  RegisterCustomSourceRequest
} from '../../core/services/ai-sources';

// Brain's `description` on each tool is written for the LLM (long, technical, part of
// its function-calling schema) -- not meant for an admin skimming a card. This is a
// frontend-only curated blurb per known tool name; a tool not listed here (a future
// addition brain registers before this map is updated) falls back to its real
// description so nothing ever shows blank.
const SHORT_DESCRIPTIONS: Record<string, string> = {
  search_sql: 'Runs read only SQL against your data, including ingested Engine tables or a connected client database.',
  search_neo4j: 'Queries the Neo4j knowledge graph for entity relationships and connections spanning multiple hops.',
  search_vector_db: 'Semantic, keyword, or hybrid search over embedded document chunks, across one collection or several at once.'
};

function emptyParameter(): CustomSourceParameter {
  return { name: '', type: 'string', required: false, description: '' };
}

@Component({
  selector: 'app-ai-sources',
  standalone: true,
  imports: [CommonModule, FormsModule, Sidebar, Navbar, ToastComponent],
  templateUrl: './ai-sources.html',
  styleUrl: './ai-sources.css'
})
export class AiSourcesPage implements OnInit {
  private aiSourcesService = inject(AiSourcesService);
  private toast = inject(Toast);

  activeTab = signal<'builtin' | 'custom'>('builtin');

  sources = signal<AiToolSource[]>([]);
  isLoading = signal(true);
  loadError = signal(false);

  customSources = signal<CustomToolSource[]>([]);
  isLoadingCustom = signal(true);
  customLoadError = signal(false);

  private togglingNames = signal<Set<string>>(new Set());
  confirmDeleteName = signal<string | null>(null);
  isDeleting = signal(false);

  showRegisterModal = signal(false);
  isViewMode = signal(false);
  isSubmitting = signal(false);

  formToolName = '';
  formDescription = '';
  formEndpointUrl = '';
  formHttpMethod: CustomSourceHttpMethod = 'GET';
  formAuthType: CustomSourceAuthType = 'none';
  formAuthHeaderName = 'Authorization';
  formAuthApiKey = '';
  formAuthToken = '';
  formAuthUsername = '';
  formAuthPassword = '';
  formParameters = signal<CustomSourceParameter[]>([emptyParameter()]);

  isTesting = signal(false);
  testResult = signal<{ statusCode: number | null; durationMs: number; body: string } | null>(null);
  testFailed = signal(false);
  // Keyed by parameter name, values kept as strings (raw input text) and coerced to the
  // declared type only when building the actual test call -- lets the admin type real
  // values (e.g. real coordinates) instead of testing against auto-generated placeholders.
  testValues = signal<Record<string, string>>({});

  httpMethods: CustomSourceHttpMethod[] = ['GET', 'POST', 'PUT'];
  authTypes: { value: CustomSourceAuthType; label: string }[] = [
    { value: 'none', label: 'None' },
    { value: 'apiKey', label: 'API key' },
    { value: 'bearer', label: 'Bearer token' },
    { value: 'basic', label: 'Basic auth' }
  ];
  paramTypes: CustomSourceParameter['type'][] = ['string', 'number', 'boolean'];

  ngOnInit() {
    this.loadSources();
    this.loadCustomSources();
  }

  setTab(tab: 'builtin' | 'custom') {
    this.activeTab.set(tab);
  }

  loadSources() {
    this.isLoading.set(true);
    this.loadError.set(false);
    this.aiSourcesService.getAll().subscribe({
      next: (res) => {
        this.isLoading.set(false);
        this.sources.set(res.data?.sources ?? []);
      },
      error: (err) => {
        this.isLoading.set(false);
        this.loadError.set(true);
        this.toast.show(err?.error?.message || 'Failed to load tools.', 'error');
      }
    });
  }

  loadCustomSources() {
    this.isLoadingCustom.set(true);
    this.customLoadError.set(false);
    this.aiSourcesService.getCustomSources().subscribe({
      next: (res) => {
        this.isLoadingCustom.set(false);
        this.customSources.set(res.data?.sources ?? []);
      },
      error: (err) => {
        this.isLoadingCustom.set(false);
        this.customLoadError.set(true);
        this.toast.show(err?.error?.message || 'Failed to load your tools.', 'error');
      }
    });
  }

  isToggling(toolName: string): boolean {
    return this.togglingNames().has(toolName);
  }

  onToggle(source: AiToolSource) {
    this.toggleAny(source.toolName, source.isActive, (updated) => {
      this.sources.update(list => list.map(s => s.toolName === source.toolName ? updated : s));
    });
  }

  onToggleCustom(source: CustomToolSource) {
    this.toggleAny(source.toolName, source.isActive, (updated) => {
      this.customSources.update(list =>
        list.map(s => s.toolName === source.toolName ? { ...s, isActive: updated.isActive, updatedAt: updated.updatedAt, updatedBy: updated.updatedBy } : s)
      );
    });
  }

  // Both built-in and custom tools toggle through the exact same brain endpoint -- once
  // registered, a custom source is just another tool name to it. This one method drives
  // both cards' switches; each caller supplies how to patch its own list on success.
  private toggleAny(toolName: string, currentActive: boolean, applyUpdate: (updated: AiToolSource) => void) {
    if (this.isToggling(toolName)) return;

    const nextActive = !currentActive;
    this.togglingNames.update(set => new Set(set).add(toolName));
    this.sources.update(list => list.map(s => s.toolName === toolName ? { ...s, isActive: nextActive } : s));
    this.customSources.update(list => list.map(s => s.toolName === toolName ? { ...s, isActive: nextActive } : s));

    this.aiSourcesService.toggle(toolName, nextActive).subscribe({
      next: (res) => {
        this.stopToggling(toolName);
        applyUpdate(res.data);
      },
      error: (err) => {
        this.stopToggling(toolName);
        // Revert the optimistic flip on both lists -- only one will actually contain
        // this tool name, the other's map is a harmless no-op.
        this.sources.update(list => list.map(s => s.toolName === toolName ? { ...s, isActive: currentActive } : s));
        this.customSources.update(list => list.map(s => s.toolName === toolName ? { ...s, isActive: currentActive } : s));
        this.toast.show(err?.error?.message || `Failed to update ${toolName}.`, 'error');
      }
    });
  }

  private stopToggling(toolName: string) {
    this.togglingNames.update(set => {
      const next = new Set(set);
      next.delete(toolName);
      return next;
    });
  }

  shortDescription(source: AiToolSource): string {
    return SHORT_DESCRIPTIONS[source.toolName] ?? source.description;
  }

  initials(toolName: string): string {
    const letters = toolName.replace(/[^a-zA-Z]/g, '');
    return (letters.slice(0, 3) || toolName.slice(0, 3)).toUpperCase();
  }

  formatUpdatedAt(value: string | null): string {
    if (!value) return 'Never changed';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Never changed';
    return `Updated ${date.toLocaleDateString()}`;
  }

  endpointSummary(source: CustomToolSource): string {
    return `${source.httpMethod} ${source.endpointUrl}`;
  }

  // -- Edit a registered source (double-click a card) -----------------------------------

  openViewModal(source: CustomToolSource) {
    this.formToolName = source.toolName;
    this.formDescription = source.description;
    this.formEndpointUrl = source.endpointUrl;
    this.formHttpMethod = source.httpMethod;
    this.formAuthType = source.authType;
    // Brain never returns stored credentials (by design -- see custom_source_routes.py's
    // CustomSourceStatus, which excludes auth_config entirely), so these stay blank in
    // view mode rather than showing a stale or fabricated value.
    this.formAuthHeaderName = 'Authorization';
    this.formAuthApiKey = '';
    this.formAuthToken = '';
    this.formAuthUsername = '';
    this.formAuthPassword = '';
    this.formParameters.set(
      source.parameters.length > 0 ? source.parameters.map(p => ({ ...p })) : [emptyParameter()]
    );
    this.testResult.set(null);
    this.testFailed.set(false);
    this.testValues.set({});
    this.isViewMode.set(true);
    this.showRegisterModal.set(true);
  }

  // -- Delete (confirmation modal) -----------------------------------------------------

  requestDelete(toolName: string) {
    this.confirmDeleteName.set(toolName);
  }

  cancelDelete() {
    if (this.isDeleting()) return;
    this.confirmDeleteName.set(null);
  }

  confirmDelete(toolName: string) {
    if (this.isDeleting()) return;
    this.isDeleting.set(true);
    this.aiSourcesService.deleteCustomSource(toolName).subscribe({
      next: () => {
        this.isDeleting.set(false);
        this.confirmDeleteName.set(null);
        this.customSources.update(list => list.filter(s => s.toolName !== toolName));
        this.toast.show(`${toolName} removed.`, 'success');
      },
      error: (err) => {
        this.isDeleting.set(false);
        this.confirmDeleteName.set(null);
        this.toast.show(err?.error?.message || `Failed to remove ${toolName}.`, 'error');
      }
    });
  }

  // -- Register new source modal ------------------------------------------------------

  openRegisterModal() {
    this.formToolName = '';
    this.formDescription = '';
    this.formEndpointUrl = '';
    this.formHttpMethod = 'GET';
    this.formAuthType = 'none';
    this.formAuthHeaderName = 'Authorization';
    this.formAuthApiKey = '';
    this.formAuthToken = '';
    this.formAuthUsername = '';
    this.formAuthPassword = '';
    this.formParameters.set([emptyParameter()]);
    this.testResult.set(null);
    this.testFailed.set(false);
    this.testValues.set({});
    this.isViewMode.set(false);
    this.showRegisterModal.set(true);
  }

  closeRegisterModal() {
    if (this.isSubmitting()) return;
    this.showRegisterModal.set(false);
    this.isViewMode.set(false);
  }

  addParameterRow() {
    this.formParameters.update(rows => [...rows, emptyParameter()]);
  }

  removeParameterRow(index: number) {
    this.formParameters.update(rows => rows.filter((_, i) => i !== index));
  }

  toggleParamRequired(index: number) {
    this.formParameters.update(rows =>
      rows.map((p, i) => i === index ? { ...p, required: !p.required } : p)
    );
  }

  get isFormValid(): boolean {
    const nameOk = /^[a-z][a-z0-9_]{2,63}$/.test(this.formToolName.trim());
    const descOk = this.formDescription.trim().length >= 10;
    const urlOk = /^https?:\/\/.+/.test(this.formEndpointUrl.trim());
    const paramsOk = this.formParameters().every(p => !p.name.trim() || /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(p.name.trim()));
    return nameOk && descOk && urlOk && paramsOk;
  }

  get toolNameHint(): string {
    if (!this.formToolName.trim()) return 'Lowercase, e.g. weather_lookup';
    return /^[a-z][a-z0-9_]{2,63}$/.test(this.formToolName.trim())
      ? ''
      : 'Lowercase letters, numbers, underscores only -- must start with a letter';
  }

  // -- Test & review (section 4) ------------------------------------------------------

  get summaryEndpoint(): string {
    const url = this.formEndpointUrl.trim() || '(not set)';
    return `${this.formHttpMethod} ${url}`;
  }

  get summaryAuth(): string {
    return this.authTypes.find(a => a.value === this.formAuthType)?.label ?? 'None';
  }

  get summaryParams(): string {
    const named = this.formParameters().filter(p => p.name.trim().length > 0);
    const requiredCount = named.filter(p => p.required).length;
    return `${named.length} defined · ${requiredCount} required`;
  }

  private buildPayload(): RegisterCustomSourceRequest {
    const parameters = this.formParameters()
      .filter(p => p.name.trim().length > 0)
      .map(p => ({ ...p, name: p.name.trim() }));

    const authConfig =
      this.formAuthType === 'apiKey' ? { headerName: this.formAuthHeaderName.trim() || 'X-Api-Key', apiKey: this.formAuthApiKey } :
      this.formAuthType === 'bearer' ? { token: this.formAuthToken } :
      this.formAuthType === 'basic' ? { username: this.formAuthUsername, password: this.formAuthPassword } :
      null;

    return {
      toolName: this.formToolName.trim(),
      description: this.formDescription.trim(),
      endpointUrl: this.formEndpointUrl.trim(),
      httpMethod: this.formHttpMethod,
      authType: this.formAuthType,
      authConfig,
      parameters
    };
  }

  // One row per currently-named parameter, for the "Test & review" section -- lets the
  // admin type real sample values (e.g. real coordinates) before sending a test request,
  // instead of only ever testing against auto-generated placeholders.
  get namedParameters(): CustomSourceParameter[] {
    return this.formParameters().filter(p => p.name.trim().length > 0);
  }

  testValueFor(name: string): string {
    return this.testValues()[name] ?? '';
  }

  updateTestValue(name: string, value: string) {
    this.testValues.update(values => ({ ...values, [name]: value }));
  }

  // Whatever the admin typed in the per-parameter test-value inputs, coerced to the
  // declared type; falls back to a placeholder for any field left blank, so "send test
  // request" always has something to send even before any real value is typed in.
  private buildSampleArgs(): Record<string, unknown> {
    const sample: Record<string, unknown> = {};
    const typed = this.testValues();
    for (const p of this.formParameters()) {
      const name = p.name.trim();
      if (!name) continue;
      const raw = typed[name]?.trim();
      if (!raw) {
        sample[name] = p.type === 'number' ? 0 : p.type === 'boolean' ? true : 'test';
        continue;
      }
      sample[name] = p.type === 'number' ? Number(raw) : p.type === 'boolean' ? raw.toLowerCase() === 'true' : raw;
    }
    return sample;
  }

  runTest() {
    if (this.isTesting() || !this.formEndpointUrl.trim()) return;

    this.isTesting.set(true);
    this.testResult.set(null);
    this.testFailed.set(false);

    this.aiSourcesService.testCustomSource(this.buildPayload(), this.buildSampleArgs()).subscribe({
      next: (res: CustomSourceTestResponse) => {
        this.isTesting.set(false);
        this.testFailed.set(!res.data.statusCode || res.data.statusCode >= 400);
        this.testResult.set({
          statusCode: res.data.statusCode,
          durationMs: res.data.durationMs,
          body: JSON.stringify(res.data.body, null, 2)
        });
      },
      error: (err) => {
        this.isTesting.set(false);
        this.testFailed.set(true);
        this.testResult.set({
          statusCode: null,
          durationMs: 0,
          body: err?.error?.message || 'Could not reach the endpoint.'
        });
      }
    });
  }

  submitRegister() {
    if (!this.isFormValid || this.isSubmitting()) return;

    this.isSubmitting.set(true);
    this.aiSourcesService.registerCustomSource(this.buildPayload()).subscribe({
      next: (res) => {
        this.isSubmitting.set(false);
        this.showRegisterModal.set(false);
        this.customSources.update(list => [...list, res.data]);
        this.toast.show(`${res.data.toolName} added.`, 'success');
      },
      error: (err) => {
        this.isSubmitting.set(false);
        this.toast.show(err?.error?.message || 'Failed to add tool.', 'error');
      }
    });
  }

  saveEdit() {
    if (!this.isFormValid || this.isSubmitting()) return;

    const toolName = this.formToolName.trim();
    this.isSubmitting.set(true);
    this.aiSourcesService.updateCustomSource(toolName, this.buildPayload()).subscribe({
      next: (res) => {
        this.isSubmitting.set(false);
        this.showRegisterModal.set(false);
        this.isViewMode.set(false);
        this.customSources.update(list => list.map(s => s.toolName === toolName ? res.data : s));
        this.toast.show(`${res.data.toolName} updated.`, 'success');
      },
      error: (err) => {
        this.isSubmitting.set(false);
        this.toast.show(err?.error?.message || `Failed to update ${toolName}.`, 'error');
      }
    });
  }
}
